/**
 * Offline Banner Component
 * Shows network status and sync state to users
 */

import { useState, useEffect } from 'react';
import { WifiOff, Cloud, CloudOff, RefreshCw, Check, AlertCircle } from 'lucide-react';
import { useOptionalAuth } from '@/hooks/use-auth';
import { useFavorites } from '@/hooks/use-favorites';

export function OfflineBanner() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showBanner, setShowBanner] = useState(false);
  const favorites = useFavorites();

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      setShowBanner(true);
      setTimeout(() => setShowBanner(false), 3000);
    };

    const handleOffline = () => {
      setIsOnline(false);
      setShowBanner(true);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    if (!navigator.onLine) {
      setShowBanner(true);
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  if (!showBanner && isOnline) {
    return null;
  }

  const getBannerContent = () => {
    if (!isOnline) {
      return {
        icon: <WifiOff className="h-4 w-4" />,
        message: 'You\'re offline. Changes will sync when you\'re back online.',
        className: 'bg-amber-500 text-white',
      };
    }

    if (favorites.isSyncing) {
      return {
        icon: <RefreshCw className="h-4 w-4 animate-spin" />,
        message: 'Syncing your data...',
        className: 'bg-forest text-white',
      };
    }

    if (favorites.syncError) {
      return {
        icon: <AlertCircle className="h-4 w-4" />,
        message: `Sync error: ${favorites.syncError}`,
        className: 'bg-red-500 text-white',
      };
    }

    return {
      icon: <Check className="h-4 w-4" />,
      message: 'Back online!',
      className: 'bg-green-500 text-white',
    };
  };

  const { icon, message, className } = getBannerContent();

  return (
    <div
      className={`fixed top-0 left-0 right-0 z-50 py-2 px-4 flex items-center justify-center gap-2 text-sm font-medium transition-all duration-300 ${className}`}
      role="status"
      aria-live="polite"
    >
      {icon}
      <span>{message}</span>
      {!isOnline && (
        <button
          onClick={() => setShowBanner(false)}
          className="ml-4 px-2 py-1 text-xs bg-white/20 rounded hover:bg-white/30 transition-colors"
          aria-label="Dismiss banner"
        >
          Dismiss
        </button>
      )}
    </div>
  );
}

export function SyncStatusIndicator() {
  const auth = useOptionalAuth();
  const favorites = useFavorites();
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  if (!auth?.isAuthenticated) {
    return null;
  }

  const getStatusIcon = () => {
    if (!isOnline) {
      return <CloudOff className="h-4 w-4 text-amber-500" />;
    }

    if (favorites.isSyncing) {
      return <RefreshCw className="h-4 w-4 text-forest animate-spin" />;
    }

    if (favorites.syncError) {
      return <AlertCircle className="h-4 w-4 text-red-500" />;
    }

    if (favorites.isCloudEnabled) {
      return <Cloud className="h-4 w-4 text-green-500" />;
    }

    return <CloudOff className="h-4 w-4 text-gray-400" />;
  };

  const getStatusText = () => {
    if (!isOnline) return 'Offline';
    if (favorites.isSyncing) return 'Syncing...';
    if (favorites.syncError) return 'Sync error';
    if (favorites.lastSyncedAt) {
      return `Synced ${formatTimeAgo(favorites.lastSyncedAt)}`;
    }
    return 'Cloud sync enabled';
  };

  return (
    <div className="flex items-center gap-1.5 text-xs text-gray-500">
      {getStatusIcon()}
      <span className="hidden sm:inline">{getStatusText()}</span>
    </div>
  );
}

function formatTimeAgo(date: Date): string {
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);

  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
}

export function OfflineIndicator() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  if (isOnline) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50 bg-amber-500 text-white px-4 py-2 rounded-lg shadow-lg flex items-center gap-2">
      <WifiOff className="h-4 w-4" />
      <span className="text-sm font-medium">Offline Mode</span>
    </div>
  );
}
